
import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import LanguageSwitcher from './LanguageSwitcher';

const Navigation: React.FC<{ solidOnScroll?: boolean }> = ({ solidOnScroll = true }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t } = useLanguage();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isRightSwipe && !isOpen) {
      setIsOpen(true);
    }
    if (isLeftSwipe && isOpen) {
      setIsOpen(false);
    }
  };

  // no admin-specific logic for landing-style navbar

  const navItems = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.stories'), href: '/stories' },
    { name: t('nav.magazine'), href: '/magazin' },
    { name: 'Shop', href: '/shop' },
    { name: t('nav.media'), href: '/mediadaten' },
    { name: t('nav.contact'), href: '/kontakt' }
  ];

  return (
    <nav 
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        solidOnScroll === false
          ? 'bg-white/95 backdrop-blur-xl border-b border-ink/5'
          : isScrolled
            ? 'bg-white/95 backdrop-blur-xl border-b border-ink/5 shadow-[0_1px_20px_-8px_rgba(18,20,22,0.15)]'
            : 'bg-transparent'
      }`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" aria-label="glide Startseite">
              <img
                src="/lovable-uploads/60ad6a07-60fb-4307-af5b-b952dcccde70.png"
                alt="glide – Das Schweizer Langlauf-Magazin"
                className={`h-8 sm:h-9 md:h-10 w-auto transition-all duration-500 ${
                  (solidOnScroll === false || isScrolled) ? '' : 'brightness-0 invert'
                }`}
                width="151"
                height="40"
                loading="eager"
              />
            </a>
          </div>

          {/* Center - Main Navigation */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-12 absolute left-1/2 -translate-x-1/2">
            {navItems.map((item) => {
              const solid = solidOnScroll === false || isScrolled;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`ice-underline text-[11px] xl:text-xs font-semibold uppercase tracking-[0.22em] transition-colors duration-300 ${
                    solid ? 'text-ink/80 hover:text-ink' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            <div className={(solidOnScroll === false || isScrolled) ? 'text-ink' : 'text-white'}>
              <LanguageSwitcher variant={(solidOnScroll === false || isScrolled) ? 'default' : 'dark'} />
            </div>
            <Link
              to="/shop#einzelausgaben"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                (solidOnScroll === false || isScrolled)
                  ? 'bg-ink text-white hover:bg-ice'
                  : 'bg-white text-ink hover:bg-snow'
              }`}
            >
              Abo
            </Link>
          </div>

          {/* Mobile menu button (show until lg) */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={(solidOnScroll === false || isScrolled) ? 'text-ink' : 'text-white'}
              aria-label="Menü"
            >
              {isOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-4 pb-6 space-y-1 bg-white border-t border-ink/5 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <div className="py-2">
                <LanguageSwitcher variant="default" />
              </div>
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block py-3 text-lg font-medium text-ink border-b border-ink/5"
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
